if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        // Initialize the color-filtered product gallery for clutch and hobo variants.
        const initialVariant = this.getSelectedVariant(this);
        if (this.isSpecialGalleryVariant(initialVariant)) {
          this.clutchGalleryGeneration = (this.clutchGalleryGeneration || 0) + 1;
          const generation = this.clutchGalleryGeneration;
          window.setTimeout(() => {
            if (generation === this.clutchGalleryGeneration) {
              this.filterSpecialGallery(this.getSelectedVariant(this), generation);
            }
          }, 0);

          this.clutchGalleryResizeHandler = () => {
            window.clearTimeout(this.clutchGalleryResizeTimer);
            this.clutchGalleryResizeTimer = window.setTimeout(() => {
              this.filterSpecialGallery(this.getSelectedVariant(this), this.clutchGalleryGeneration);
            }, 80);
          };
          window.addEventListener('resize', this.clutchGalleryResizeHandler, { passive: true });
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        if (this.clutchGalleryResizeHandler) {
          window.removeEventListener('resize', this.clutchGalleryResizeHandler);
        }
        window.clearTimeout(this.clutchGalleryResizeTimer);
        window.clearTimeout(this.clutchGalleryFilterTimer);
        this.clutchGalleryGeneration = (this.clutchGalleryGeneration || 0) + 1;
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          // Invalidate stale gallery work BEFORE updating the media DOM.
          this.clutchGalleryGeneration = (this.clutchGalleryGeneration || 0) + 1;
          const generation = this.clutchGalleryGeneration;
          window.clearTimeout(this.clutchGalleryFilterTimer);

          // For clutch and hobo variants, updateMedia() filters the exact color photos.
          this.updateMedia(html, variant?.featured_media?.id, variant);

          // Safety re-apply after the DOM swap, and keep normal behavior for all other products.
          if (this.isSpecialGalleryVariant(variant)) {
            this.clutchGalleryFilterTimer = window.setTimeout(() => {
              if (generation === this.clutchGalleryGeneration) {
                this.filterSpecialGallery(variant, generation);
              }
            }, 0);
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId, variant = null) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // For clutch and hobo variants, filter BEFORE activating any media.
        // This prevents Shopify's native gallery from scrolling through unrelated color images.
        if (variant && this.isSpecialGalleryVariant(variant)) {
          this.filterSpecialGallery(variant, this.clutchGalleryGeneration);
        } else {
          this.querySelector(`media-gallery`)?.setActiveMedia?.(
            `${this.dataset.section}-${variantFeaturedMediaId}`,
            true
          );
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      isClutchVariant(variant) {
        return new Set([
          '50464993345603',
          '50464993378371',
          '50464993411139',
          '50464993443907',
          '50464993476675',
          '50464993509443',
          '50464993542211',
          '50466695086147',
        ]).has(String(variant?.id || ''));
      }

      isHoboVariant(variant) {
        return new Set([
          '67622519373891', // Grey / White
          '67622281674819', // Yellow
        ]).has(String(variant?.id || ''));
      }

      isSpecialGalleryVariant(variant) {
        return this.isClutchVariant(variant) || this.isHoboVariant(variant);
      }

      filterClutchGallery(variant, generation = this.clutchGalleryGeneration) {
        return this.filterSpecialGallery(variant, generation);
      }

      filterHoboGallery(variant, generation = this.clutchGalleryGeneration) {
        return this.filterSpecialGallery(variant, generation);
      }

      filterSpecialGallery(variant, generation = this.clutchGalleryGeneration) {
        if (generation !== this.clutchGalleryGeneration) return;

        const variantId = String(variant?.id || '');

        const galleryGroups = {
          // Clutch: 8 colors × 4 photos.
          '50464993345603': ['46184559476803', '46185359540291', '46185359573059', '46185359605827'],
          '50464993378371': ['46184456650819', '46185359638595', '46185359671363', '46185359704131'],
          '50464993411139': ['46186612457539', '46185359736899', '46185359769667', '46185359802435'],
          '50464993443907': ['46181215502403', '46185359835203', '46185359867971', '46185359900739'],
          '50464993476675': ['46184561999939', '46185359933507', '46185359966275', '46185359999043'],
          '50464993509443': ['46181219598403', '46186350116931', '46186350149699', '46186350182467'],
          '50464993542211': ['46185292726339', '46185360130115', '46185360162883', '46185360195651'],
          '50466695086147': ['46186527424579', '46186465951811', '46186465984579', '46186467459139'],

          // Hobo: Grey / White = 3 studio + 1 lifestyle; Yellow = 4 selected product views.
          '67622519373891': ['46241344815171', '46241342357571', '46241342390339', '46241342423107'],
          '67622281674819': ['46241252147267', '46241253294147', '46241254506563', '46241255391299'],
        };

        const wantedMediaIds = new Set(galleryGroups[variantId] || []);
        if (!wantedMediaIds.size) return;

        const mediaIdTail = (value) => {
          const raw = String(value || '');
          const match = raw.match(/(?:-|\/)?(\d+)$/);
          return match ? match[1] : raw;
        };

        this.querySelectorAll('media-gallery').forEach((gallery) => {
          const mainSlider = gallery.querySelector('slider-component[id^="GalleryViewer-"]');
          const mainList = gallery.querySelector('ul[id^="Slider-Gallery-"]');
          if (!mainSlider || !mainList) return;

          const galleryItems = Array.from(mainList.querySelectorAll(':scope > li[data-media-id]'));
          const matchingItems = galleryItems.filter((item) =>
            wantedMediaIds.has(mediaIdTail(item.dataset.mediaId))
          );

          // Safety fallback: never leave customers with a blank gallery if a media ID changes.
          if (!matchingItems.length) {
            galleryItems.forEach((item) => {
              item.style.display = '';
              item.setAttribute('aria-hidden', 'false');
              item.classList.remove('is-active');
            });

            const current = gallery.querySelector('.slider-counter--current');
            const totalNode = gallery.querySelector('.slider-counter--total');
            if (current) current.textContent = '1';
            if (totalNode) totalNode.textContent = String(galleryItems.length);
            mainSlider.slider?.scrollTo?.({ left: 0, behavior: 'auto' });
            return;
          }

          const itemById = new Map(
            galleryItems.map((item) => [mediaIdTail(item.dataset.mediaId), item])
          );
          const orderedItems = Array.from(wantedMediaIds)
            .map((id) => itemById.get(id))
            .filter(Boolean);

          galleryItems.forEach((item) => {
            const visible = matchingItems.includes(item);
            item.style.display = visible ? '' : 'none';
            item.setAttribute('aria-hidden', visible ? 'false' : 'true');
            item.classList.remove('is-active');
          });

          orderedItems.forEach((item, index) => {
            mainList.appendChild(item);
            item.style.display = '';
            item.setAttribute('aria-hidden', 'false');
            item.classList.toggle('is-active', index === 0);
          });

          mainSlider.slider?.scrollTo?.({ left: 0, behavior: 'auto' });
          mainSlider.resetPages?.();

          const thumbnailsSlider = gallery.querySelector('slider-component[id^="GalleryThumbnails-"]');
          if (thumbnailsSlider) {
            const thumbnails = Array.from(
              thumbnailsSlider.querySelectorAll('ul[id^="Slider-Thumbnails-"] > li[data-target]')
            );
            const thumbnailList = thumbnailsSlider.querySelector('ul[id^="Slider-Thumbnails-"]');
            const thumbnailById = new Map(
              thumbnails.map((thumbnail) => [mediaIdTail(thumbnail.dataset.target), thumbnail])
            );

            thumbnails.forEach((thumbnail) => {
              const mediaId = mediaIdTail(thumbnail.dataset.target);
              const visible = wantedMediaIds.has(mediaId);
              thumbnail.style.display = visible ? '' : 'none';
              thumbnail.setAttribute('aria-hidden', visible ? 'false' : 'true');
              thumbnail.querySelector('button')?.setAttribute(
                'aria-current',
                visible && mediaId === mediaIdTail(orderedItems[0]?.dataset.mediaId) ? 'true' : 'false'
              );
            });

            Array.from(wantedMediaIds).forEach((id) => {
              const thumbnail = thumbnailById.get(id);
              if (thumbnail) {
                thumbnailList?.appendChild(thumbnail);
                thumbnail.style.display = '';
                thumbnail.setAttribute('aria-hidden', 'false');
              }
            });

            thumbnailsSlider.slider?.scrollTo?.({ left: 0, behavior: 'auto' });
            thumbnailsSlider.resetPages?.();
          }

          const current = gallery.querySelector('.slider-counter--current');
          const totalNode = gallery.querySelector('.slider-counter--total');
          if (current) current.textContent = orderedItems.length ? '1' : '0';
          if (totalNode) totalNode.textContent = String(orderedItems.length);

          orderedItems.forEach((item, index) => {
            item.classList.toggle('is-active', index === 0);
          });
          mainSlider.slider?.scrollTo?.({ left: 0, behavior: 'auto' });
        });

        const modal = this.productModal?.querySelector('.product-media-modal__content');
        if (modal) {
          modal.querySelectorAll('[data-media-id]').forEach((item) => {
            const mediaId = mediaIdTail(item.dataset.mediaId);
            const visible = wantedMediaIds.has(mediaId);
            item.style.display = visible ? '' : 'none';
            item.setAttribute('aria-hidden', visible ? 'false' : 'true');
          });
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
